import { useNavigate } from "react-router-dom";
import { FC, useCallback, useRef, useState } from "react";
import cn from "classnames";
import {
  Button,
  Container,
  DropAcceptedFunc,
  FileItem,
  FileUpload,
  notification,
  Nullable,
  useWillUnmount,
  useDidUpdate,
  useFileQueue,
} from "@epcnetwork/core-ui-kit";

import {
  getAcceptedFilesText,
  getPipelineRoute,
  getSupportedFormats,
  onFileRedirect,
  prepareSuppressValue,
} from "utils";
import { useTracking } from "hooks";
import { SMARTLOOK_EVENTS } from "constants/smartlook.constants";
import { PIPELINES_LIST_PAGE } from "constants/routes.constants";
import { postSuppressFile } from "api";
import { PipelineHeaderSection } from "../components/pipeline-header-section/pipeline-header-section";
import {
  SuppressFileItem,
  MatchAndEliminateFileInfo,
  PipelineMatchAndEliminateData,
  MatchAndEliminateRequestData,
} from "./match-and-eliminate.types";
import { supportedFormats, getInitialData } from "./match-and-eliminate.constants";
import { MatchAndEliminateFormModal } from "./form-modal/match-and-eliminate-form-modal";

import styles from "./match-and-eliminate.module.css";

export const MatchAndEliminatePage: FC = () => {
  const componentIsMounted = useRef(true);
  const navigate = useNavigate();

  const { pipelineSubmitEvent } = useTracking();

  const pipeline = getPipelineRoute("Match and Eliminate");

  const {
    files,
    createInitialFile,
    addFiles,
    updateFiles,
    removeFiles,
    clearEntity,
    getItem,
    isEntityInConfiguration,
    isEntityFinished,
    hasAllConfigured,
    submitOneByOne,
    hasAllSuccessFiles,
  } = useFileQueue<SuppressFileItem, PipelineMatchAndEliminateData>(
    pipeline.title,
    postSuppressFile,
  );

  useWillUnmount(() => {
    componentIsMounted.current = false;
  });

  useDidUpdate(() => {
    if (!isEntityFinished) return;

    handleRedirect();
  }, [isEntityFinished]);

  const [editedFile, setEditedFile] = useState<Nullable<SuppressFileItem>>(null);

  const handleAcceptedDrop: DropAcceptedFunc = useCallback(
    (acceptedFiles) => {
      addFiles(
        acceptedFiles.map((file) => createInitialFile(file, { data: getInitialData(file) })),
      );
    },
    [addFiles, createInitialFile],
  );

  const handleClearClick = () => {
    notification.confirm(
      "Delete files",
      `Are you sure you want to delete all ${files.length} file(s)?`,
      {
        onOk: clearEntity,
        icon: "delete",
      },
    );
  };

  const handleConfigureItem = useCallback(
    (id: string) => {
      const item = getItem(id);
      if (!item) return;
      setEditedFile(item);
    },
    [getItem],
  );

  const handleModalSubmit = useCallback(
    ({ id, ...rest }: SuppressFileItem) => {
      updateFiles({ id, file: rest });
      setEditedFile(null);
    },
    [updateFiles],
  );

  const handleCloseModal = useCallback(() => setEditedFile(null), []);

  const handleSubmit = () => {
    const data = files.map((file) => {
      const formData = new FormData();

      const { fileName, emailIndex, databases, suppress } = file.data;
      const suppressData: MatchAndEliminateRequestData = {
        columnIndex: emailIndex,
        databases,
        suppress: prepareSuppressValue(emailIndex, suppress),
      };

      formData.append("fileInfo", JSON.stringify(suppressData));
      formData.append("file", file.originalFile, fileName);

      return {
        id: file.id,
        data: formData,
      };
    });

    submitOneByOne(data);
    pipelineSubmitEvent(SMARTLOOK_EVENTS.matchAndEliminateSubmit, files);
  };

  const handleRedirect = () => {
    onFileRedirect({ files, clearEntity, navigate });
  };

  const handleRedirectToPipelines = () => {
    navigate(PIPELINES_LIST_PAGE.path);
  };

  return (
    <Container>
      <div className={styles.container}>
        <PipelineHeaderSection pipeline={pipeline} />

        <div className={styles.form}>
          <FileUpload
            className={cn(styles.dropZone, { [styles.uploadZone]: files.length })}
            uploadedFilesLength={files.length}
            subtitle={getAcceptedFilesText(getSupportedFormats(supportedFormats))}
            accept={supportedFormats}
            onDropAccepted={handleAcceptedDrop}
            exceedFilesOption="splice-with-error"
            disabled={!isEntityInConfiguration}
            preventDropOnDocument
            multiple
          />
          <div className={styles.fileList}>
            {files.map(({ id, originalFile, data, ...rest }) => {
              const showFileContent = data.databases?.length > 0 || data.emailIndex >= 0;

              return (
                <FileItem
                  {...rest}
                  key={id}
                  id={id}
                  file={originalFile}
                  onCrossClick={removeFiles}
                  onSetValuesClick={handleConfigureItem}
                  onEditValuesClick={handleConfigureItem}
                >
                  {showFileContent && (
                    <div className={styles.column}>
                      {data.emailIndex >= 0 && (
                        <div className={styles.additionalInfo}>
                          <span>Selected email column:</span> {data.emailIndex + 1}
                        </div>
                      )}
                      {data.databases && data.databases.length > 0 && (
                        <div className={styles.additionalInfo}>
                          <span>Databases:</span> {data.databases.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </FileItem>
              );
            })}
          </div>
          {editedFile && (
            <MatchAndEliminateFormModal
              file={editedFile}
              setClose={handleCloseModal}
              onSubmitClick={handleModalSubmit}
            />
          )}

          <div className={styles.buttons}>
            <Button
              appearance="secondary"
              onClick={!files.length ? handleRedirectToPipelines : handleClearClick}
              disabled={isEntityFinished && !hasAllSuccessFiles}
            >
              Cancel
            </Button>
            {!isEntityFinished && (
              <Button
                onClick={handleSubmit}
                disabled={!hasAllConfigured || !files.length}
                loading={!isEntityInConfiguration}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};
